// CommonMethods.js - Utility functions for global search and other common operations

import moment from 'moment-timezone';
import Cookies from 'universal-cookie';
import constants from './constants';
import { isPrIpv4FormatInvalid } from '../ui-validation/prVpnValidation';
import { isValidPrIpv6Address } from '../ui-validation/prIpv6Validation';

const cookies = new Cookies();

// Error message mapping
const errorMessages = {
  60001: 'Too many results found. Please refine your search.',
  60002: 'No devices found matching your search',
  60009: 'No clients found matching your search',
  60015: 'No devices found matching your search',
  60016: 'No clients found matching your search',
  8056: 'Session expired. Please log in again.',
  // Support User Error Codes
  44026: 'Another support agent is already impersonating this customer. Please try again later.',
  44034: 'Your impersonation request is pending with the active support user.',
  44035: 'The active support user rejected your impersonation request.',
  44036: 'Another support user has already requested access to this customer account. Please try again later.',
  44030: 'Your Support Session is revoked. Tap OK to log in again.',
  44032: 'Session started successfully',
  44005: 'Support access updated successfully',
  44018: 'Session ended successfully',
  44013: 'Session ended successfully',
  44031: 'Session ended successfully',
  // VLAN delete error codes
  94031: 'The VLAN is currently used by either OpenVPN or VPN users or Dual WAN Rules and cannot be deleted at this time.',
  // R11b X7: BE twin of the VLAN IPv6 binding guard (VLAN_IPV6_USED_BY_IP_SEC_SITE_TO_SITE).
  94058:
    "This VLAN's IPv6 settings are used by an IPsec site-to-site connection. Remove the VLAN from the connection before you change its IPv6 type or turn off its unique local address.",
  7025: 'Cannot delete VLAN with access ports configured on PR device',
  default: 'An error occurred while processing your request.'
};

// Get error message by response code
export const getErrMessage = (responseCode) => {
  return errorMessages[responseCode] || errorMessages.default;
};

/**
 * PR30 family detection. The PR30 router was renamed to PR30SG, so devices now
 * report the model as "PR30SG" while older units may still report "PR30". Both
 * share the same chassis/port layout (WAN1, LAN1–LAN3, LAN4/WAN2), so treat them
 * identically for device images and port mapping.
 */
export const isPr30FamilyModel = (model) => {
  const modelUpper = String(model || '').toUpperCase();
  return modelUpper === 'PR30' || modelUpper === 'PR30SG';
};

const normalizeWirelessSupportFlag = (value, defaultValue = 'No') => {
  if (value === 'Yes') return 'Yes';
  if (value === 'No') return 'No';
  return defaultValue;
};

/** Parse is6GhzSupported API details into separate 6 GHz and MLO flags. */
export const parseBandSupport = (details = {}) => ({
  is6GhzSupported: normalizeWirelessSupportFlag(details.isSupported),
  isMloSupported: normalizeWirelessSupportFlag(details.isMloSupported)
});

/** Persist 6 GHz and MLO support flags used by SSID / wireless onboarding flows. */
export const saveBandSupport = ({ is6GhzSupported, isMloSupported }) => {
  const sixGhzValue = is6GhzSupported || 'No';
  const mloValue = isMloSupported || 'No';
  localStorage.setItem('is6GhzSupported', sixGhzValue);
  localStorage.setItem('isMloSupported', mloValue);
};

// Date formatting utility
export const DateFilter = (dateString) => {
  const replaceStrings = {
    PM: 'p.m.',
    pm: 'p.m.',
    AM: 'a.m.',
    am: 'a.m.'
  };
  return dateString
    ? dateString.replace(/PM|pm|AM|am/gi, function (matched) {
        return replaceStrings[matched];
      })
    : dateString;
};

// Return the string as it is if it not empty or undefined else return N/A
export const CheckBlankString = (string, defaultValue) => {
  if (!string || string == '' || string == 'Not Applicable') {
    if (!defaultValue) return 'N/A';
    return defaultValue;
  } else {
    return string;
  }
};

// Function for MAC Address format
export const GetMacFormat = (macAddress) => {
  const format = /[ `!@#$%^&*()_+\-=[\]{};:'"\\|,.<>/?~]/;
  const replaceAllUnsafe = (input, searchRegex, replacement) => {
    return (input || '').replace(searchRegex, replacement);
  };

  let finalMac = 'N/A';
  let macAdd = '';

  if (typeof macAddress === 'object') {
    if (macAddress && macAddress.length > 0) {
      for (let i = 0; i < macAddress.length; i++) {
        macAdd = macAdd + replaceAllUnsafe(String(macAddress[i]), /[ `!@#$%^&*()_+\-=[\]{};:'"\\|,.<>/?~]/g, ':') + ', ';
        finalMac = macAdd.slice(0, -2).toLowerCase();
      }
    } else {
      finalMac = 'N/A';
    }
  } else {
    const macAddressVal = macAddress ? macAddress.toString() : '';
    if (macAddressVal) {
      if (format.test(macAddressVal)) {
        macAdd = replaceAllUnsafe(macAddressVal, /[ `!@#$%^&*()_+\-=[\]{};:'"\\|,.<>/?~]/g, ':');
        finalMac = macAdd.toLowerCase();
      } else if (macAddressVal.length === 12) {
        macAdd =
          macAddressVal.substring(0, 2) +
          ':' +
          macAddressVal.substring(2, 4) +
          ':' +
          macAddressVal.substring(4, 6) +
          ':' +
          macAddressVal.substring(6, 8) +
          ':' +
          macAddressVal.substring(8, 10) +
          ':' +
          macAddressVal.substring(10, 12);
        finalMac = macAdd.toLowerCase();
      } else {
        // Fallback: try inserting ':' every two chars
        finalMac = macAddressVal
          .match(/.{1,2}/g)
          .join(':')
          .toLowerCase();
      }
    } else {
      finalMac = 'N/A';
    }
  }
  return finalMac;
};

// Function for MAC Address input formatting with auto-colon insertion
export const formatMacAddressInput = (value) => {
  // Remove non-alphanumeric characters and trim spaces (keep case intact)
  value = value.trim().replace(/[^a-zA-Z0-9]/g, '');

  // Format the value into MAC address format
  let formattedValue = '';
  for (let i = 0; i < value.length; i += 2) {
    if (i > 0) formattedValue += ':'; // Add colon after the first pair
    formattedValue += value.substring(i, i + 2);
  }

  // Ensure that the formatted value doesn't exceed 17 characters
  if (formattedValue.length > 17) {
    formattedValue = formattedValue.substring(0, 17); // Trim to 17 characters if needed
  }

  return formattedValue.toUpperCase();
};

// Function to validate MAC address format
export const validateMacAddressFormat = (macAddress) => {
  // MAC address must be exactly in format XX:XX:XX:XX:XX:XX
  const macPattern = /^([0-9A-F]{2}:){5}[0-9A-F]{2}$/;
  return macPattern.test(macAddress.toUpperCase());
};

// Helper function for MAC address change handling
export const handleMacAddressInputChange = (value, setter, field) => {
  const formatted = formatMacAddressInput(value);
  setter((prev) => ({ ...prev, [field]: formatted }));
};

// Parse firmware version strings into numeric segments for stable ordering checks.
export const parseVersionSegments = (value) => {
  if (value === null || typeof value === 'undefined') return [];
  return String(value)
    .split(/[^\d]+/)
    .filter(Boolean)
    .map((segment) => Number(segment))
    .filter((segment) => !Number.isNaN(segment));
};

export const compareFirmwareVersions = (first, second) => {
  const firstSegments = parseVersionSegments(first);
  const secondSegments = parseVersionSegments(second);
  if (firstSegments.length && secondSegments.length) {
    const maxLength = Math.max(firstSegments.length, secondSegments.length);
    for (let index = 0; index < maxLength; index += 1) {
      const firstValue = firstSegments[index] ?? 0;
      const secondValue = secondSegments[index] ?? 0;
      if (firstValue !== secondValue) {
        return firstValue > secondValue ? 1 : -1;
      }
    }
    return 0;
  }
  return String(first).localeCompare(String(second), undefined, { numeric: true, sensitivity: 'base' });
};

// Derive human-readable radio band label from various hints
export const GetRadioLabel = (radio, band, channel) => {
  const norm = (v) => (v !== undefined && v !== null ? String(v).toLowerCase() : '');
  const r = norm(radio);
  const b = norm(band);
  const ch = Number(channel);

  // If radio already a readable string
  if (r.includes('2.4')) return '2.4 GHz';
  if (r.includes('2g')) return '2.4 GHz';
  if (r.includes('5')) return '5 GHz';
  if (r.includes('6')) return '6 GHz';

  // Codes used by legacy implementations
  // 0 → 2.4 GHz, 3/4/7/9 → 5 GHz, 5 → 6 GHz, 8 → Both
  if (['0'].includes(b)) return '2.4 GHz';
  if (['3', '4', '7', '9'].includes(b)) return '5 GHz';
  if (['5'].includes(b)) return '6 GHz';
  if (['8'].includes(b)) return 'Both';

  // Infer from channel when available
  if (!Number.isNaN(ch)) {
    if (ch > 0 && ch <= 14) return '2.4 GHz';
    if ((ch >= 32 && ch <= 173) || (ch >= 36 && ch <= 165)) return '5 GHz';
    if (ch >= 183) return '6 GHz';
  }

  return 'N/A';
};

// Convert memory size (bytes or kb) to human-readable units
export const ConvertMemorySize = (value, currentUnit = 'byte', isSpeed = false, qosSpeed = false) => {
  if (value === undefined || value === null || value === '') return 'N/A';
  const v = parseInt(value, 10);
  if (Number.isNaN(v)) return 'N/A';

  const as = (n) => (isSpeed || qosSpeed ? n : n.toFixed(2));
  if (currentUnit === 'byte') {
    if (v >= 1024 ** 4) return `${as(v / 1024 ** 4)} TB`;
    if (v >= 1024 ** 3) return `${as(v / 1024 ** 3)} GB`;
    if (v >= 1024 ** 2) return `${as(v / 1024 ** 2)} MB`;
    if (v >= 1024) return `${as(v / 1024)} KB`;
    return `${v} Byte`;
  }
  if (currentUnit === 'kb') {
    if (v >= 1024 ** 3) return `${as(v / 1024 ** 3)} TB`;
    if (v >= 1024 ** 2) return `${as(v / 1024 ** 2)} GB`;
    if (v >= 1024) return `${as(v / 1024)} MB`;
    return `${v} KB`;
  }
  return `${v}`;
};

// Convert seconds to a human-readable duration string like "1day 0hr 30mins 6secs"
export const ConvertSecondToDuration = (secondsInput) => {
  if (secondsInput === undefined || secondsInput === null || secondsInput === '') return 'N/A';
  const totalSeconds = Number(secondsInput);
  if (Number.isNaN(totalSeconds) || totalSeconds < 0) return 'N/A';

  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  const parts = [];
  if (days > 0) parts.push(`${days}day`);
  if (hours > 0) parts.push(`${hours}hr`);
  if (minutes > 0) parts.push(`${minutes}mins`);
  parts.push(`${seconds}secs`);
  return parts.join(' ');
};

// Map codes to human-readable security authentication labels
export const mapAuthentication = (code) => {
  const key = String(code ?? '');
  const map = {
    0: 'Open',
    8: 'WPA2 Enterprise',
    12: 'WPA/WPA2 Mixed',
    32: 'WPA2 Personal',
    48: 'WPA2 Personal Mixed',
    80: 'WPA3 Personal',
    96: 'WPA3 Personal Mixed (WPA2 + WPA3)',
    112: 'WPA3 Enterprise',
    128: 'WPA3 Enterprise Standard',
    144: 'WPA3 Enterprise Mixed (WPA2 + WPA3)'
  };
  return map[key] || 'N/A';
};

// Map encryption codes to labels
export const mapEncryption = (code) => {
  const key = String(code ?? '');
  const map = {
    0: 'Open',
    2: 'TKIP',
    4: 'CCMP-AES',
    6: 'TKIP+CCMP-AES',
    8: 'CCMP-256',
    10: 'GCMP-128',
    12: 'GCMP-256'
  };
  return map[key] || 'N/A';
};

export const getEncryptionForAuthType = (authType) => {
  const key = String(authType ?? '');
  // GCMP-256 stays with 112 only, the AP rejects the save otherwise.
  if (key === '128' || key === '144') return '4';
  if (key === '112' || key === '80') return '12'; // WPA3 -> GCMP-256
  if (key === '8') return '4'; // WPA2 Enterprise -> CCMP-AES only
  return '6'; // Open / WPA2 Personal / WPA3 Personal Mixed
};

// Map 802.11w PMF codes
export const mapPmf = (code) => {
  const key = String(code ?? '');
  const map = { 0: 'Disabled', 1: 'Optional', 2: 'Mandatory' };
  return map[key] || 'N/A';
};

// Map radio band codes to human-readable labels.
// Replicates the legacy radioEnum + connBand/isTriBand/radioBand selection logic.
export const mapRadioBand = (connBand, isTriBand, radioBand) => {
  const radioEnum = {
    0: '2.4 GHz',
    3: '5 GHz low',
    4: '5 GHz high',
    5: '6 GHz',
    7: '5 GHz',
    8: 'Both',
    9: '5 GHz'
  };

  let key;
  if (connBand != null && connBand !== '') {
    const band = String(connBand);
    if (band === '0' || band === '4' || band === '5') {
      key = band;
    } else if (band === '3') {
      key = String(isTriBand) === '1' ? band : '9';
    } else {
      key = band;
    }
  } else {
    key = radioBand != null ? String(radioBand) : '';
  }

  return radioEnum[key] || 'N/A';
};

// Format MAC address with message
export const GetMsgMacFormat = (message) => {
  if (!message) return '';

  // Replace MAC address patterns with formatted version
  const macPattern = /([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})/g;
  return message.replace(macPattern, (match) => {
    return match.toUpperCase();
  });
};

// Device redirection utility
export const redirectToDevice = (deviceName, serialNo, deviceId, deviceType, model, redirectType, priStatus, remoteMgmtSt) => {
  // Set device info in localStorage
  const newDeviceInfo = {
    model: model,
    serial: serialNo,
    name: deviceName
  };

  localStorage.setItem('dashboardNewDeviceInfo', JSON.stringify(newDeviceInfo));
  localStorage.setItem('deviceModel', model);
  localStorage.setItem('isDnsSupport', '0');
  localStorage.setItem('isSpeedTestSupport', '0');
  localStorage.setItem('selectedDeviceSerial', serialNo);
  localStorage.setItem('selectedDeviceId', deviceId);
  localStorage.setItem('deviceName', deviceName);
  localStorage.setItem('selectedDeviceType', deviceType);

  let deviceUrl = '';

  // Determine if we should use the new MUI device page for APs
  const isMuiContext = (() => {
    if (typeof window === 'undefined') return false;
    const path = window.location?.pathname || '';
    const href = window.location?.href || '';
    return path.includes('/mui/') || href.includes('/mui/') || window.__INSIGHT_MUI__;
  })();
  const getMuiBaseUrl = () => {
    const fullUrl = window.location?.href || '';
    const match = fullUrl.match(/^(.*\/mui\/)/);
    if (match && match[1]) return match[1];
    if (fullUrl.includes('/mui/')) {
      const index = fullUrl.indexOf('/mui/');
      return fullUrl.substring(0, index + 5);
    }
    const origin = window.location?.origin || '';
    return `${origin}/mui/`;
  };

  if (isMuiContext) {
    if (deviceType === 'AP') {
      const selectedDevice = {
        id: deviceId,
        serialNo,
        name: deviceName,
        model,
        status: priStatus,
        deviceType,
        priStatus,
        remoteManagement: remoteMgmtSt
      };
      localStorage.setItem(constants.SELECTED_DEVICE, JSON.stringify(selectedDevice));
      if (deviceId) {
        localStorage.setItem(constants.SELECTED_DEVICE_ID, deviceId);
      }
      localStorage.setItem('selectedDeviceName', deviceName);
      localStorage.setItem('selectedDeviceSerial', serialNo);
      localStorage.setItem('selectedDeviceType', deviceType);
      localStorage.setItem('deviceName', deviceName);
      localStorage.setItem('deviceModel', model);

      // AP routes now follow the same pattern as other device types
      window.location.href = `${getMuiBaseUrl()}devices/${deviceType}/summary`;
    } else {
      window.location.href = `${getMuiBaseUrl()}devices/${deviceType}/summary`;
    }
    return;
  }

  // If the device is smart pro or locally managed, redirect to device list page (legacy context only)
  if (remoteMgmtSt === '0' || remoteMgmtSt === '2' || priStatus === 2) {
    if (window.location.hash.includes('#/devices/dash')) {
      document.location.reload();
    } else {
      window.location.href = '#/devices/dash';
    }
    return;
  }
  // Determine device URL based on device type
  switch (deviceType) {
    case 'AP':
      deviceUrl = redirectType === 'troubleShoot' ? '#/devices/accessPoint/troubleshoot' : '#/devices/accessPoint/summary';
      break;
    case 'NAS':
      // Clear NAS-specific localStorage
      localStorage.removeItem('NASIcon');
      localStorage.removeItem('NASname');
      localStorage.removeItem('nas_device_status');
      localStorage.removeItem('nasOfflineLastSeen');
      localStorage.removeItem('nas_last_refreshed');
      localStorage.removeItem('nas_priStatus');
      deviceUrl = '#/devices/nas/summary';
      break;
    case 'SW':
      deviceUrl = redirectType === 'troubleShoot' ? '#/devices/switch/troubleshoot' : '#/devices/switch/summary';
      break;
    case 'ORBI':
      deviceUrl = redirectType === 'troubleShoot' ? '#/devices/orbi/troubleshoot' : '#/devices/orbi/summary';
      break;
    case 'airBridge':
      deviceUrl = redirectType === 'troubleShoot' ? '#/devices/ab/troubleshoot' : '#/devices/ab/summary';
      break;
    case 'BR':
      deviceUrl = redirectType === 'troubleShoot' ? '#/devices/br/DNSLookup' : '#/devices/br/summary';
      break;
    case 'PR':
      deviceUrl = redirectType === 'troubleShoot' ? '#/devices/br/troubleshoot' : '#/devices/br/summary';
      break;
    default:
      deviceUrl = '#/devices/dash';
  }

  // Navigate to the device URL
  if (window.location.hash.includes(deviceUrl)) {
    document.location.reload();
  } else {
    window.location.href = deviceUrl;
  }
};

// Check whether a data item represents a navigable device (has an identifier and a type)
export const isDeviceItem = (item) => item && (item.id || item.deviceId) && item.deviceType;

const CLICKABLE_DEVICE_TYPES = [
  constants.DEVICE_TYPES.PR,
  constants.DEVICE_TYPES.AP,
  constants.DEVICE_TYPES.MHS,
  constants.DEVICE_TYPES.SW,
  constants.DEVICE_TYPES.ORBI,
  constants.DEVICE_TYPES.AV,
  'BR'
];

// Returns true if the device name should be clickable.
// - Only for device types in CLICKABLE_DEVICE_TYPES.
// - Not clickable when unmanaged (remoteMgmtSt '0' or '2').
// - Not clickable when pending / onboarding (priStatus 43).
export const isDeviceNameClickable = (deviceType, remoteMgmtSt, priStatus) => {
  if (!CLICKABLE_DEVICE_TYPES.includes(deviceType)) {
    return false;
  }

  if (remoteMgmtSt === '0' || remoteMgmtSt === '2') return false;
  if (priStatus === 43) return false;

  return true;
};

// Handle device name click from device list
// options.returnPath: path to navigate back to after e.g. delete (e.g. /devices or /organization/OrgName/devices)
export const handleDeviceNameClick = (device, navigate, options = {}) => {
  if (!device) return;
  // Store required parameters for getBRSummaryData API
  if (device?.orgName) {
    localStorage.setItem('savedOrgName', device.orgName);
  }
  if (localStorage.getItem('CalledFrom') !== 'deviceGroupLevel') {
    localStorage.setItem('savedLocationName', device.networkName);
  }
  localStorage.setItem('OrgId', device.orgId);
  localStorage.setItem('selectedDeviceId', device.id);
  localStorage.setItem('selectedDeviceType', device.deviceType);
  localStorage.setItem('selectedDeviceName', device.name);
  localStorage.setItem('networkId', device.networkId);
  localStorage.setItem('selectedDeviceSerial', device.serialNo);
  localStorage.setItem('currentNetwork', device.networkId);
  localStorage.setItem('organizationId', device.orgId);
  localStorage.setItem(constants.SELECTED_DEVICE, JSON.stringify(device));

  // Navigate to device detail page - common pattern for all device types
  const path = `/devices/${device.deviceType}/summary`;
  const state = options.returnPath != null ? { returnPath: options.returnPath } : undefined;
  navigate(path, state != null ? { state } : undefined);
};

/**
 * Stack name-click from the Devices table (org / all-org / location). Mirrors handleDeviceNameClick:
 * switch the hierarchy scope to the stack's LOCATION (network) so the LHS tree + breadcrumb follow the
 * device into its location, then navigate to the Stack Detail page. Clicking a standalone device does
 * this; a stack must too. The location comes from a present member's device row (buildStackRow copies
 * orgId/networkId/orgName/networkName onto the row). `ctx` carries the OrganizationContext setters
 * (updateOrganizationContext + refreshHierarchyTree) so this stays a plain util, not a hook.
 */
export const handleStackNameClick = (stackRow, navigate, ctx = {}, options = {}) => {
  if (!stackRow) return;
  // Persist selection so the breadcrumb/title row can show the stack name on the detail page,
  // mirroring the standalone device dashboard pattern.
  if (stackRow.stackId) localStorage.setItem('selectedStackId', String(stackRow.stackId));
  if (stackRow.name || stackRow.stackName) localStorage.setItem('selectedStackName', String(stackRow.name || stackRow.stackName));
  const { updateOrganizationContext, refreshHierarchyTree } = ctx;
  const orgId = stackRow.orgId || '';
  const networkId = stackRow.networkId || '';
  // Only switch scope when the stack's network is known (org/all-org rows carry it via a present
  // member; a location-scope click is already in the right network, so this is a harmless no-op there).
  if (networkId) {
    const selectedType = { id: networkId, label: stackRow.networkName || '', type: 'Loc', icon: 'Loc' };
    if (stackRow.orgName) localStorage.setItem('savedOrgName', stackRow.orgName);
    localStorage.setItem('savedLocationName', stackRow.networkName || '');
    localStorage.setItem('selectedType', JSON.stringify(selectedType));
    localStorage.setItem('scopeId', '2');
    localStorage.setItem('OrgId', orgId);
    localStorage.setItem('organizationId', orgId);
    localStorage.setItem('networkId', networkId);
    localStorage.setItem('currentNetwork', networkId);
    if (typeof updateOrganizationContext === 'function') {
      updateOrganizationContext(orgId, '2', networkId, selectedType);
    }
    if (typeof refreshHierarchyTree === 'function') {
      refreshHierarchyTree();
    }
  }
  const path = `/locationDevice/stack/${stackRow.stackId}`;
  navigate(path, options.returnPath != null ? { state: { returnPath: options.returnPath } } : undefined);
};

/**
 * PRJCBUGEN-65198 — click routing for the dashboard "Top Switches by …" cards. A switch that belongs
 * to a stack must open the Stack dashboard, not its own device dashboard: once a stack forms, the
 * stack is the management unit, and the per-device page is not where the user expects to land.
 * The row is enriched with `stackId` (switchConfiguration `_id`) + `stackName` by the caller; when
 * it carries neither this is an ordinary device click and falls through unchanged.
 *
 * `ctx` carries the OrganizationContext setters that handleStackNameClick needs for the scope switch.
 */
export const handleTopChartDeviceClick = (row, navigate, ctx = {}) => {
  if (!row) return;
  if (row.stackId) {
    // handleStackNameClick prefers `name` for the persisted stack name, but a chart row's `name` is
    // the switch label ("<serial> (<stack name>)"), so hand it the stack's own name instead.
    handleStackNameClick({ ...row, name: row.stackName || '', stackName: row.stackName || '' }, navigate, ctx);
    return;
  }
  if (isDeviceItem(row)) {
    handleDeviceNameClick({ ...row, id: row.id ?? row.deviceId }, navigate);
  }
};

/** Persist stack manager switch as the selected device so device-scoped alarms APIs resolve correctly. */
export const persistStackManagerAlarmContext = (member, stackName = '') => {
  const deviceId = member?.deviceId || member?.id;
  if (!deviceId) return;
  const name = member?.name || member?.deviceName || stackName || '';
  localStorage.setItem('selectedDeviceId', String(deviceId));
  localStorage.setItem('selectedDeviceType', 'SW');
  localStorage.setItem('deviceName', name);
  localStorage.setItem('selectedDeviceSerial', member?.serialNo || '');
  localStorage.setItem(
    constants.SELECTED_DEVICE,
    JSON.stringify({
      id: deviceId,
      deviceId,
      deviceType: 'SW',
      serialNo: member?.serialNo || '',
      name,
      deviceName: name
    })
  );
};

// Role management utilities
export const getUserRole = () => {
  return cookies.get('userRole') || 'generic';
};

export const checkUserRole = (requiredRole) => {
  const userRole = getUserRole();
  const roleHierarchy = {
    admin: 5,
    secondaryAdmin: 4,
    owner: 3,
    rwManager: 2,
    roManager: 2,
    lineManager: 1,
    voucherAdmin: 1,
    preVoucherAdmin: 1,
    generic: 0
  };

  return roleHierarchy[userRole] >= roleHierarchy[requiredRole];
};

// Organization and network data utilities
export const setNetworkData = (locationId) => {
  if (locationId) {
    localStorage.setItem('currentNetworkImg', locationId.icon);
    localStorage.setItem('totalOnline', locationId.totalOnline);
    localStorage.setItem('totalDevice', locationId.totalDevices);
    localStorage.setItem('currentNetworkName', locationId.networkName);
    localStorage.setItem('currentWirelessRegion', locationId.wirelessRegion);
    cookies.set('currentNetwork', locationId.networkId, { secure: true });
  }
};

/**
 * Validates DDNS update interval based on unit
 * @param {number} interval - The interval value
 * @param {string} unit - The unit ('minutes', 'hours', or 'days')
 * @returns {object} - { isValid: boolean, isBelow5Minutes: boolean, isAbove30Days: boolean, totalMinutes: number }
 */
export const validateDDNSUpdateInterval = (interval, unit = 'minutes') => {
  const numInterval = Number(interval);

  // Convert to minutes for comparison
  let totalMinutes = 0;
  if (unit === 'minutes') {
    totalMinutes = numInterval;
  } else if (unit === 'hours') {
    totalMinutes = numInterval * 60;
  } else if (unit === 'days') {
    totalMinutes = numInterval * 24 * 60;
  }

  // Check if interval is below 5 minutes or above 30 days (43200 minutes)
  const isBelow5Minutes = !Number.isNaN(numInterval) && numInterval > 0 && totalMinutes < 5;
  const isAbove30Days = !Number.isNaN(numInterval) && totalMinutes > 43200;

  // Unit-specific validation ranges
  const invalidMinutes = unit === 'minutes' && (numInterval < 5 || numInterval > 43200);
  const invalidHours = unit === 'hours' && (numInterval < 1 || numInterval > 720);
  const invalidDays = unit === 'days' && (numInterval < 1 || numInterval > 30);

  const isValid = !Number.isNaN(numInterval) && numInterval > 0 && !invalidMinutes && !invalidHours && !invalidDays;

  return {
    isValid,
    isBelow5Minutes,
    isAbove30Days,
    totalMinutes,
    invalidMinutes,
    invalidHours,
    invalidDays
  };
};

export const setOrganizationData = (organizationId) => {
  if (organizationId) {
    cookies.set('organization_Id', organizationId.orgId, { secure: true });
    localStorage.setItem('currentOrgName', organizationId.orgName);
    localStorage.setItem('currentOrgLogo', organizationId.orgLogo);
  }
};

// Text highlighting utility for search results
export const highlightSearchText = (text, searchText) => {
  if (!searchText || !text) return text;

  const regex = new RegExp(`(${searchText})`, 'gi');
  return text.replace(regex, '<mark>$1</mark>');
};

// Validate search input
export const validateSearchInput = (input) => {
  // Allow only specific characters: alphanumeric, -, _, ., :, and space
  // Space and . not allowed as first character
  const regex = /^(?![ .])[A-Za-z0-9_\-. :]*$/;
  return regex.test(input);
};

// Check if organization and location are available
// This would typically make an API call to check organization availability; for now it is always true.
// (The old try / catch around `return true` had an unreachable catch branch; same behaviour.)
export const checkOrgAndLocAvailable = async () => true;

// Get current URL context for feature level validation
export const getCurrentUrlContext = () => {
  const currentPath = window.location.hash || window.location.pathname;

  if (
    currentPath.includes('organization/dashboard') ||
    currentPath.includes('accountManagement') ||
    currentPath.includes('dashboard/account') ||
    currentPath.includes('dashboard/myDevices') ||
    currentPath.includes('organization/devices') ||
    currentPath.includes('organization/details') ||
    currentPath.includes('/reports/list')
  ) {
    return 'account';
  }

  if (currentPath.includes('organization')) {
    return 'organization';
  }

  return 'location';
};

// Feature access validation
export const validateFeatureAccess = (feature) => {
  const userRole = getUserRole();
  const urlContext = getCurrentUrlContext();

  // Check premium features
  if (feature.premiumFeature && (userRole === 'generic' || userRole === 'preVoucherAdmin' || userRole === 'supportAgent')) {
    return {
      canAccess: false,
      reason: 'premium'
    };
  }

  // Check feature level access
  if (feature.featureLevel === 'location' && urlContext === 'account') {
    return {
      canAccess: false,
      reason: 'location'
    };
  }

  if (feature.featureLevel === 'organization' && urlContext === 'account') {
    return {
      canAccess: false,
      reason: 'organization'
    };
  }

  return {
    canAccess: true,
    reason: null
  };
};

// Security utilities
export const sanitizeInput = (input) => {
  return input.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
};

// Local storage management
export const GLOBAL_SEARCH_CLIENT_NAV_EVENT = 'globalSearchClientNavigate';
export const GLOBAL_SEARCH_SSID_SELECT_EVENT = 'globalSearchSsidSelect';
export const OPEN_GLOBAL_SEARCH_SSID_HANDOFF = 'OPEN_GLOBAL_SEARCH_SSID_HANDOFF';
export const GLOBAL_SEARCH_SSID_SHOW_PROMPT_EVENT = 'globalSearchSsidShowPrompt';
export const GLOBAL_SEARCH_MANUAL_OPEN_EVENT = 'globalSearchManualOpen';

const GLOBAL_SEARCH_SSID_PICKER_KEY = 'globalSearchSsidPicker';
const GLOBAL_SEARCH_SSID_SCOPE_KEY = 'globalSearchSsidScope';
const GLOBAL_SEARCH_SSID_SUB_FEATURE_KEY = 'globalSearchSsidSubFeature';
const GLOBAL_SEARCH_SSID_SHOW_PROMPT_KEY = 'globalSearchSsidShowPrompt';

export const readGlobalSearchSsidState = () => ({
  isFromGlobalSearch: localStorage.getItem('fromGlobalSearchComponent') === '1',
  isSsidPicker: localStorage.getItem(GLOBAL_SEARCH_SSID_PICKER_KEY) === '1',
  isSsidPageHandoff:
    localStorage.getItem('fromGlobalSearchComponent') === '1' && localStorage.getItem('redirectedFrom') === 'globalSearchSsid',
  query: (localStorage.getItem('globalSearchQuery') || '').trim(),
  ssidScope: (localStorage.getItem(GLOBAL_SEARCH_SSID_SCOPE_KEY) || '').trim(),
  subFeatureName: (localStorage.getItem(GLOBAL_SEARCH_SSID_SUB_FEATURE_KEY) || '').trim()
});

/**
 * Persist SSID global-search handoff before navigating to the SSID page.
 * @param {{ featureTitle: string, subFeatureName?: string, ssidScope?: 'organization'|'network', showPagePrompt?: boolean }} payload
 */
export const setGlobalSearchSsidHandoff = ({ featureTitle, subFeatureName = '', ssidScope = '', showPagePrompt = false }) => {
  localStorage.setItem('fromGlobalSearchComponent', '1');
  localStorage.setItem('redirectedFrom', 'globalSearchSsid');
  if (featureTitle) {
    localStorage.setItem('globalSearchQuery', featureTitle);
  }
  if (subFeatureName) {
    localStorage.setItem(GLOBAL_SEARCH_SSID_SUB_FEATURE_KEY, subFeatureName);
  }
  if (ssidScope) {
    localStorage.setItem(GLOBAL_SEARCH_SSID_SCOPE_KEY, ssidScope);
  }
  if (showPagePrompt) {
    sessionStorage.setItem(GLOBAL_SEARCH_SSID_SHOW_PROMPT_KEY, '1');
  } else {
    sessionStorage.removeItem(GLOBAL_SEARCH_SSID_SHOW_PROMPT_KEY);
  }
};

export const shouldShowGlobalSearchSsidPagePrompt = () => sessionStorage.getItem(GLOBAL_SEARCH_SSID_SHOW_PROMPT_KEY) === '1';

export const clearGlobalSearchSsidPagePrompt = () => {
  sessionStorage.removeItem(GLOBAL_SEARCH_SSID_SHOW_PROMPT_KEY);
};

export const clearGlobalSearchSsidHandoff = () => {
  localStorage.removeItem(GLOBAL_SEARCH_SSID_PICKER_KEY);
  localStorage.removeItem(GLOBAL_SEARCH_SSID_SCOPE_KEY);
  localStorage.removeItem(GLOBAL_SEARCH_SSID_SUB_FEATURE_KEY);
  clearGlobalSearchSsidPagePrompt();
};

export const clearSearchRelatedStorage = () => {
  localStorage.removeItem('fromGlobalSearchComponent');
  localStorage.removeItem('globalSearchQuery');
  clearGlobalSearchSsidHandoff();
};

/**
 * Reset global search from SSID picker mode when the user manually opens search on an SSID page.
 */
export const resetGlobalSearchFromSsidPicker = () => {
  clearSearchRelatedStorage();
  localStorage.removeItem('redirectedFrom');
  window.dispatchEvent(new CustomEvent(GLOBAL_SEARCH_MANUAL_OPEN_EVENT));
};

// Group information utility
// device-group detection by checking scope, selectedType, cookies, and localStorage.
export const getGroupInfo = () => {
  const localGroupId = localStorage.getItem('currentDeviceGrpId') || localStorage.getItem('currentGroupId');
  const localGroupName = localStorage.getItem('savedDeviceGrpName') || localStorage.getItem('currentGroupName');

  // Check if we're in a custom group context
  const customGroupId = localStorage.getItem('currentDeviceGrpId');
  const customGroupName = localStorage.getItem('currentDeviceGrpName');

  return {
    groupId: customGroupId || localGroupId || '',
    groupName: customGroupName || localGroupName || ''
  };
};

// Time zone conversion utility
export const toTimeZone = (time, timezone) => {
  const format = 'dddd, MMMM D YYYY, hh:mm A';
  if (timezone) {
    return moment(time * 1000)
      .tz(timezone)
      .format(format);
  } else {
    return moment(time * 1000).format(format);
  }
};

// Map Secondary VLAN Type code to label
export const getSecondaryVlanTypeLabel = (code) => {
  const value = code !== undefined && code !== null ? String(code) : '';
  switch (value) {
    case '0':
      return 'Not Applicable';
    case '1':
      return 'Public';
    case '2':
      return 'Employee';
    case '3':
      return 'Guest';
    default:
      return '-';
  }
};

// IP Calculation utilities
export const GetBroadcastIp = {
  // Convert IP address to 32-bit integer
  ipToInt: (ip) => {
    return ip.split('.').reduce((int, octet) => (int << 8) + parseInt(octet, 10), 0) >>> 0;
  },

  // Convert 32-bit integer back to IP address string
  intToIp: (int) => {
    return [(int >>> 24) & 255, (int >>> 16) & 255, (int >>> 8) & 255, int & 255].join('.');
  },

  // Calculate network IP address given IP and subnet mask
  calculateNetworkIP: (ipAddress, subnetMask) => {
    try {
      const ipInt = GetBroadcastIp.ipToInt(ipAddress);
      const maskInt = GetBroadcastIp.ipToInt(subnetMask);
      const networkInt = ipInt & maskInt;
      return GetBroadcastIp.intToIp(networkInt);
    } catch (error) {
      return ipAddress; // Return original IP on error
    }
  },

  // Calculate broadcast IP address given network IP and subnet mask
  calculateBroadcastIP: (networkIP, subnetMask) => {
    try {
      const networkInt = GetBroadcastIp.ipToInt(networkIP);
      const maskInt = GetBroadcastIp.ipToInt(subnetMask);
      const wildcardInt = ~maskInt >>> 0; // Bitwise NOT of subnet mask
      const broadcastInt = networkInt | wildcardInt;
      return GetBroadcastIp.intToIp(broadcastInt);
    } catch (error) {
      return networkIP; // Return network IP on error
    }
  }
};

// Default export object with all utilities
const CommonMethods = {
  getErrMessage,
  DateFilter,
  CheckBlankString,
  GetMacFormat,
  ConvertSecondToDuration,
  GetMsgMacFormat,
  redirectToDevice,
  getUserRole,
  checkUserRole,
  setNetworkData,
  setOrganizationData,
  highlightSearchText,
  validateSearchInput,
  checkOrgAndLocAvailable,
  getCurrentUrlContext,
  validateFeatureAccess,
  sanitizeInput,
  clearSearchRelatedStorage,
  GLOBAL_SEARCH_CLIENT_NAV_EVENT,
  toTimeZone,
  getSecondaryVlanTypeLabel,
  isDeviceItem
};

// to add custom VLAN in select dropdown if management VLAN is not default
export const showCustomValues = (vlanConfig, selectedMgmtVlanId) => {
  const list = [...vlanConfig];
  const findVlanId = vlanConfig.filter((item) => item.vlanId == selectedMgmtVlanId);
  if (findVlanId.length == 0) {
    const spreadList = list[0];
    const assignList = selectedMgmtVlanId;
    if (assignList != '1') {
      const addValue = { ...spreadList };
      addValue.vlanId = assignList;
      addValue.isManagementVlan = '1';
      addValue.name = 'Custom Management VLAN';
      addValue.qosProfile = 'Data';
      list.push(addValue);
    }
  }
  return list;
};
// function to select the type of VLAN ID
export const changeStatus = (value, list, selectedMgmtVlanId) => {
  let selectedVlanidType;
  if (list.filter((data) => data.vlanId == value).length > 0) {
    const { isManagementVlan, qosProfile } = list.filter((data) => data.vlanId == value)[0];
    if (isManagementVlan == '0') {
      if (qosProfile == 'Video') {
        selectedVlanidType = '4';
      } else if (qosProfile == 'Voice') {
        selectedVlanidType = '5';
      } else if (qosProfile == 'Data') {
        selectedVlanidType = '6';
      }
      return selectedVlanidType;
    }
  }
  if (value == 1) {
    selectedVlanidType = '1';
  } else if (value == 4089) {
    selectedVlanidType = '4';
  } else if (value == selectedMgmtVlanId) {
    selectedVlanidType = '2';
  } else {
    selectedVlanidType = '3';
  }
  return selectedVlanidType;
};
// function to display the range of Network Address
export const subnetChangeAddEdit = (subnet, ipAddress) => {
  const selectedSubNet = subnet;
  const ip = ipAddress.split('.');
  const subnetMask = subnet.split('.');
  const wildCard = [];
  const startIp = (ip[0] & subnetMask[0]) + '.' + (ip[1] & subnetMask[1]) + '.' + (ip[2] & subnetMask[2]) + '.' + (ip[3] & subnetMask[3]); //calculating the start ip based on ipaddress and subnet
  const startIPRange =
    (ip[0] & subnetMask[0]) + '.' + (ip[1] & subnetMask[1]) + '.' + (ip[2] & subnetMask[2]) + '.' + ((ip[3] & subnetMask[3]) + 2);
  const startIpArr = startIp.split('.');
  for (let net = 0; net < subnetMask.length; net++) {
    const binary = ('00000000' + (subnetMask[net] >>> 0).toString(2)).slice(-8);
    const replaceCharacter = { 0: '1', 1: '0' };
    const changeToDecimal = binary.replace(/[01]/g, (m) => replaceCharacter[m]);
    const decimal = parseInt(changeToDecimal, 2).toString(10);
    wildCard[net] = decimal;
  }
  const networkIp = startIp.split('.');
  const endIp =
    (networkIp[0] | wildCard[0]) +
    '.' +
    (networkIp[1] | wildCard[1]) +
    '.' +
    (networkIp[2] | wildCard[2]) +
    '.' +
    ((networkIp[3] | wildCard[3]) - 1);
  let ipNotMatched = true;
  for (let ipoct = 0; ipoct < 4; ipoct++) {
    if (ip[ipoct] != startIpArr[ipoct]) {
      ipNotMatched = false;
    }
  }

  return {
    startIPRange: startIPRange,
    endIp: endIp,
    subnet: selectedSubNet,
    startIp: startIp,
    ipNotMatched: ipNotMatched
  };
};
// function to generate IP addresses between start and end IP
export const ipsBetweenStartandEndIp = (startIp, endIp) => {
  const ipToInt = (ip) => {
    return ip.split('.').reduce((int, octet) => (int << 8) + parseInt(octet, 10), 0) >>> 0;
  };

  const intToIp = (int) => {
    return [(int >>> 24) & 255, (int >>> 16) & 255, (int >>> 8) & 255, int & 255].join('.');
  };

  const start = ipToInt(startIp);
  const end = ipToInt(endIp);
  const ipList = [];

  if (start > end) {
    return [];
  }

  for (let i = start; i <= end; i++) {
    ipList.push(intToIp(i));
  }

  return ipList;
};

// function to check for duplicate array:
export const isArrayDuplicate = (arr) => {
  if (new Set(arr).size != arr.length) {
    return true;
  } else {
    return false;
  }
};
// function to check for empty object
export const isEmpty = (obj) => {
  if (Object.entries(obj).length == 0) {
    return true;
  } else {
    return false;
  }
};
// function to combain two array's and add id, policy, selectiontype:
export const combineMACArrays = (arr1 = [], arr2 = []) => {
  return [
    ...arr1.map((item) => ({
      ...item,
      id: Date.now() + item.mac + 'allowed',
      policy: 'allow',
      selectedForRemoval: false,
      type: 'manual'
    })),
    ...arr2.map((item) => ({
      ...item,
      id: Date.now() + item.mac + 'denied',
      policy: 'deny',
      selectedForRemoval: false,
      type: 'manual'
    }))
  ];
};
// function used to update ars data / multicast array
export const parseArsdataMulticastArray = (arsData, defaultValArs) => {
  const newArsData = { ...arsData };
  const densityRateEnum = ['0', '0', '0'];
  for (let index = 0; index < 3; index++) {
    if (!arsData['wlan' + index] && defaultValArs) {
      newArsData['wlan' + index] = {
        ...defaultValArs['wlan' + index],
        ['status']: '0',
        ['densityLevel']: densityRateEnum[index],
        ['multicastRate']: defaultValArs['wlan' + index]?.fixedMulticastRateDef || '0'
      };
    } // validate for wlan keys
    if (newArsData['wlan' + index]) {
      // Clone the band object to break references to DEFAULT_ARS_VALUE
      newArsData['wlan' + index] = { ...newArsData['wlan' + index] };
      // Ensure fixedMulticastRateArray exists, then map to a NEW array of option objects
      // Never mutate the original array or its items to avoid changing DEFAULT_ARS_VALUE
      const fixedMulticastRateArraySource =
        newArsData['wlan' + index].fixedMulticastRateArray ||
        (defaultValArs && defaultValArs['wlan' + index]?.fixedMulticastRateArray) ||
        [];

      const mappedRateArray = Array.isArray(fixedMulticastRateArraySource)
        ? fixedMulticastRateArraySource.map((opt) => {
            if (!opt) return opt;
            // If already in { label, value } shape, keep as-is (copy to avoid mutations)
            if (Object.prototype.hasOwnProperty.call(opt, 'label')) {
              return { ...opt };
            }
            // Convert from default { code, value: "X Mbps"/"Auto" } to UI shape
            return { ...opt, label: opt.value, value: opt.code };
          })
        : [];

      newArsData['wlan' + index].fixedMulticastRateArray = mappedRateArray;
    }
  }
  return newArsData;
};
// function used to add ars key values to save ssid on the basis of band value
export const addArsBandKeys = (arsData, radioBand, arsDefaultVal) => {
  const arsVal = {};
  // add key wlan 0 only when band == 2.4 Ghz or Both
  if (radioBand == '0' || radioBand == '8' || radioBand == '9' || radioBand == '12') {
    arsVal['wlan0'] = {
      status: arsData.wlan0 ? arsData.wlan0.status : '0',
      densityLevel: arsData.wlan0 ? arsData.wlan0.densityLevel : '0',
      multicastRate: arsData.wlan0 ? arsData.wlan0.multicastRate : arsDefaultVal.wlan0.fixedMulticastRateDef
    };
  }
  // add key wlan 1 only when band == 5Ghz or Both
  if (radioBand == '7' || radioBand == '8' || radioBand == '10' || radioBand == '12') {
    arsVal['wlan1'] = {
      status: arsData.wlan1 ? arsData.wlan1.status : '0',
      densityLevel: arsData.wlan1 ? arsData.wlan1.densityLevel : '0',
      multicastRate: arsData.wlan1 ? arsData.wlan1.multicastRate : arsDefaultVal.wlan1.fixedMulticastRateDef
    };
    // add key wlan 2 only for triband devices // Band == 5Ghz or Both
    arsVal['wlan2'] = {
      status: arsData.wlan2 ? arsData.wlan2.status : '0',
      densityLevel: arsData.wlan2 ? arsData.wlan2.densityLevel : '0',
      multicastRate: arsData.wlan2 ? arsData.wlan2.multicastRate : arsDefaultVal.wlan2.fixedMulticastRateDef
    };
  }
  // add key for the band combination of 6Ghz
  if (radioBand == '9' || radioBand == '10' || radioBand == '11' || radioBand == '12') {
    arsVal['wlan2_6'] = {
      status: arsData.wlan2_6 ? arsData.wlan2_6.status : '0',
      densityLevel: arsData.wlan2_6 ? arsData.wlan2_6.densityLevel : '0',
      multicastRate: arsData.wlan2_6 ? arsData.wlan2_6.multicastRate : arsDefaultVal.wlan2_6.fixedMulticastRateDef
    };
  }
  return arsVal;
};

// funciton to return the density info to the user
export const functionalDensityInfoMessage = (bandSelected, value, intl) => {
  const returnText = {
    Environment: '',
    Compatibility: '',
    Performance: '',
    Coverage: ''
  };
  switch (value) {
    case '0':
      returnText.Environment = intl.formatMessage({ id: 'veryLow' });
      returnText.Compatibility = bandSelected == 'wlan0' ? intl.formatMessage({ id: 'bgnax' }) : intl.formatMessage({ id: 'anacax' });
      returnText.Performance = intl.formatMessage({ id: 'reduced' });
      returnText.Coverage = intl.formatMessage({ id: 'veryWide' });
      break;
    case '1':
      returnText.Environment = intl.formatMessage({ id: 'low' });
      returnText.Compatibility = bandSelected == 'wlan0' ? intl.formatMessage({ id: 'bgnax' }) : intl.formatMessage({ id: 'anacax' });
      returnText.Performance = intl.formatMessage({ id: 'moderate' });
      returnText.Coverage = intl.formatMessage({ id: 'wide' });
      break;
    case '2':
      returnText.Environment = intl.formatMessage({ id: 'mediumCommon' });
      returnText.Compatibility = bandSelected == 'wlan0' ? intl.formatMessage({ id: 'bgnax' }) : intl.formatMessage({ id: 'anacax' });
      returnText.Performance = intl.formatMessage({ id: 'good' });
      returnText.Coverage = intl.formatMessage({ id: 'average' });
      break;
    case '3':
      returnText.Environment = intl.formatMessage({ id: 'high' });
      returnText.Compatibility = bandSelected == 'wlan0' ? intl.formatMessage({ id: 'gnax' }) : intl.formatMessage({ id: 'anacax' });
      returnText.Performance = intl.formatMessage({ id: 'veryGood' });
      returnText.Coverage = intl.formatMessage({ id: 'narrow' });
      break;
    case '4':
      returnText.Environment = intl.formatMessage({ id: 'veryHigh' });
      returnText.Compatibility = bandSelected == 'wlan0' ? intl.formatMessage({ id: 'gnax' }) : intl.formatMessage({ id: 'anacax' });
      returnText.Performance = intl.formatMessage({ id: 'optimal' });
      returnText.Coverage = intl.formatMessage({ id: 'verynarrow' });
      break;
  }

  return `${intl.formatMessage({ id: 'environment' })}: ${intl.formatMessage({ id: 'density' })} - ${returnText.Environment}, ${intl.formatMessage({ id: 'compatibility' })} - ${returnText.Compatibility}, ${intl.formatMessage({ id: 'overallPerformance' })} - ${returnText.Performance}, ${intl.formatMessage({ id: 'coverage' })} - ${returnText.Coverage}`;
};

export const safeInt = (value) => {
  if (value === undefined || value === null || value === '') return 0;
  const normalized = String(value).trim();
  const n = Number(normalized);
  return Number.isFinite(n) ? Math.trunc(n) : 0;
};

// IP range validation utility - checks if an IP is within start-end range
export const checkIpInClientsRange = (startIp, endIp, ipAddress) => {
  if (!startIp || !endIp || !ipAddress) {
    return false;
  }
  try {
    // Use existing ipToInt function for consistency
    const startIpNum = GetBroadcastIp.ipToInt(startIp);
    const endIpNum = GetBroadcastIp.ipToInt(endIp);
    const ipAddressNum = GetBroadcastIp.ipToInt(ipAddress);

    return ipAddressNum >= startIpNum && ipAddressNum <= endIpNum;
  } catch (error) {
    return false;
  }
};

/**
 * Network address from subnet mask and host IP (legacy PrOpenVPN / CommonMethods: `calculateNetworkIp(netmask, ipAddress)`).
 * @param {string} netmask
 * @param {string} ipAddress
 * @returns {string}
 */
export const calculateNetworkIp = (netmask, ipAddress) => {
  if (!netmask || !ipAddress) return '';
  try {
    return GetBroadcastIp.calculateNetworkIP(String(ipAddress).trim(), String(netmask).trim());
  } catch {
    return '';
  }
};

// --- OpenVPN pool / TAP utilities (legacy CommonMethods; used by PR OpenVPN and aligned with old imports) ---

function openVpnPoolIpToUint32(ip) {
  if (!ip || typeof ip !== 'string') return 0;
  try {
    return GetBroadcastIp.ipToInt(ip.trim());
  } catch {
    return 0;
  }
}

/** WAN gateway rows for client-pool vs WAN overlap (legacy `createArrayOfWanAddress`). */
export const createArrayOfWanAddress = (info) => {
  if (!info) return [];
  const wanAddressList = [];
  ['One', 'Two'].forEach((key) => {
    const gateway = info[`wan${key}Gateway`];
    const netmask = info[`wan${key}Netmask`];
    if (gateway != null && String(gateway).trim() !== '') {
      wanAddressList.push({
        ipaddr: String(gateway).trim(),
        netmask: netmask != null && String(netmask).trim() !== '' ? String(netmask).trim() : '255.255.255.0'
      });
    }
  });
  return wanAddressList;
};

/**
 * True if VPN client pool [startIp,endIp] overlaps any VLAN/WAN subnet (legacy `clientToSitePoolRange`).
 * @param {{ netmask: string, ipaddr: string }[]} vlanList
 */
export const clientToSitePoolRange = (vlanList, startIp, endIp) => {
  if (!Array.isArray(vlanList) || !startIp || !endIp) return false;
  const enteredStartIP = openVpnPoolIpToUint32(startIp);
  const enteredEndIP = openVpnPoolIpToUint32(endIp);

  for (let i = 0; i < vlanList.length; i++) {
    const items = vlanList[i];
    if (!items?.netmask || !items?.ipaddr) continue;
    const { startIPRange, endIp: endIpRange } = subnetChangeAddEdit(items.netmask, items.ipaddr);
    const startNumeric = openVpnPoolIpToUint32(startIPRange);
    const endNumeric = openVpnPoolIpToUint32(endIpRange);
    if ((enteredStartIP >= startNumeric && enteredStartIP <= endNumeric) || (enteredEndIP >= startNumeric && enteredEndIP <= endNumeric)) {
      return true;
    }
  }
  return false;
};

function openVpnGetNetworkAndBroadcastUint32(ip, netmask) {
  const ipNum = openVpnPoolIpToUint32(ip);
  const netmaskNum = openVpnPoolIpToUint32(netmask);
  const invertedNetmask = ~netmaskNum >>> 0;
  const networkAddress = (ipNum & netmaskNum) >>> 0;
  const broadcastAddress = (ipNum | invertedNetmask) >>> 0;
  return { networkAddress, broadcastAddress };
}

function openVpnIsIpInUint32Range(ip, networkUint, broadcastUint) {
  const ipNum = openVpnPoolIpToUint32(ip);
  return ipNum >= networkUint && ipNum <= broadcastUint;
}

/** VLAN DHCP pools require start < end (matches location VLAN DHCP form validation). */
function isOpenVpnDhcpPoolRangeValid(dhcpStart, dhcpEnd) {
  if (!dhcpStart || !dhcpEnd || dhcpStart === dhcpEnd) return false;
  try {
    const startNum = openVpnPoolIpToUint32(dhcpStart);
    const endNum = openVpnPoolIpToUint32(dhcpEnd);
    return startNum < endNum;
  } catch {
    return false;
  }
}

export function filterOpenVpnTapInterfaceList(interfaceList = []) {
  return (interfaceList || []).filter((row) => !['wan', 'wan1'].includes(String(row?.interface || '').toLowerCase()));
}

/**
 * TAP DHCP range vs VLAN DHCP / network (legacy `checkIpRanges`).
 * @returns {{ isValid: boolean, isValidNetwork: boolean, reason?: 'overlap_dhcp' | 'inside_vlan_subnet' }}
 */
export const checkIpRanges = (tapDhcpStart, tapDhcpEnd, vlanInfo) => {
  let isValid = true;
  let isValidNetwork = true;
  /** @type {'overlap_dhcp' | 'inside_vlan_subnet' | null} */
  let reason = null;
  if (!Array.isArray(vlanInfo) || !vlanInfo.length) {
    return { isValid, isValidNetwork, reason };
  }
  vlanInfo.forEach((vlan) => {
    const normalizedVlan = normalizeOpenVpnVlanEntry(vlan) || vlan;
    const { ipaddr, netmask, dhcpStart, dhcpEnd } = normalizedVlan;
    if (!ipaddr || !netmask) return;
    const { networkAddress, broadcastAddress } = openVpnGetNetworkAndBroadcastUint32(ipaddr, netmask);
    const hasDhcpPool = isOpenVpnDhcpPoolRangeValid(dhcpStart, dhcpEnd);
    if (hasDhcpPool) {
      if (
        checkIpInClientsRange(dhcpStart, dhcpEnd, tapDhcpStart) ||
        checkIpInClientsRange(dhcpStart, dhcpEnd, tapDhcpEnd) ||
        checkIpInClientsRange(tapDhcpStart, tapDhcpEnd, dhcpStart) ||
        checkIpInClientsRange(tapDhcpStart, tapDhcpEnd, dhcpEnd)
      ) {
        isValid = false;
        reason = 'overlap_dhcp';
      }
    }
    if (
      openVpnIsIpInUint32Range(tapDhcpStart, networkAddress, broadcastAddress) &&
      openVpnIsIpInUint32Range(tapDhcpEnd, networkAddress, broadcastAddress)
    ) {
      isValidNetwork = false;
      if (reason !== 'overlap_dhcp') {
        reason = 'inside_vlan_subnet';
      }
    }
  });
  return { isValid, isValidNetwork, reason };
};

/**
 * Normalize VLAN id for comparison (3, "3", "VLAN3" → "3").
 * @param {string|number|null|undefined} value
 * @returns {string}
 */
export const normalizeVlanIdForCompare = (value) => {
  if (value == null || value === '') return '';
  const raw = String(value).trim().toUpperCase();
  const vlanMatch = raw.match(/^VLAN0*(\d+)$/);
  if (vlanMatch) return String(parseInt(vlanMatch[1], 10));
  const numeric = String(value).trim();
  if (/^\d+$/.test(numeric)) return String(parseInt(numeric, 10));
  return numeric;
};

/** @returns {boolean} */
export const vlanIdsMatch = (a, b) => {
  const na = normalizeVlanIdForCompare(a);
  const nb = normalizeVlanIdForCompare(b);
  return na !== '' && nb !== '' && na === nb;
};

/**
 * True when vlanOrId matches any entry in vpnAccessVlanIds (handles VLAN3 vs 3).
 * @param {string|number|object} vlanOrId
 * @param {Array<string|number>} vpnAccessVlanIds
 */
export const isVlanIdInVpnAccessList = (vlanOrId, vpnAccessVlanIds) => {
  const raw = vlanOrId && typeof vlanOrId === 'object' ? (vlanOrId.vlanId ?? vlanOrId.id ?? vlanOrId.vlanid) : vlanOrId;
  const target = normalizeVlanIdForCompare(raw);
  if (target === '') return false;
  return (vpnAccessVlanIds || []).some((assigned) => vlanIdsMatch(target, assigned));
};

/**
 * Normalize one OpenVPN vlanInfo row from GET_PR_OPENVPN_INFO (field names vary by source).
 * @param {object} vlan
 * @returns {object|null}
 */
export const normalizeOpenVpnVlanEntry = (vlan) => {
  if (!vlan || typeof vlan !== 'object') return null;
  const vlanId = vlan.vlanid ?? vlan.vlanId ?? vlan.id;
  const ipaddr = String(vlan.ipaddr ?? vlan.ipAddress ?? vlan.lanIp ?? vlan.gateway ?? '').trim();
  const netmask = String(vlan.netmask ?? vlan.subnetMask ?? vlan.lanSubnet ?? '').trim();
  const dhcpStart = String(vlan.dhcpStart ?? vlan.dhcpServer?.startIp ?? vlan.startIP ?? vlan.startIp ?? '').trim();
  const dhcpEnd = String(vlan.dhcpEnd ?? vlan.dhcpServer?.endIp ?? vlan.endIP ?? vlan.endIp ?? '').trim();
  const name = String(vlan.name ?? vlan.vlanName ?? '').trim();
  const normalizedId = vlanId != null ? normalizeVlanIdForCompare(vlanId) : '';
  const interfaceLabel = String(vlan.interface ?? (normalizedId ? `VLAN${normalizedId}` : name)).trim();
  return {
    ...vlan,
    vlanid: vlanId,
    ipaddr,
    netmask,
    dhcpStart,
    dhcpEnd,
    name,
    interface: interfaceLabel
  };
};

/**
 * Merge vlanInfo with interfaceList so TAP VLAN dropdown values resolve reliably.
 * @param {object[]} vlanInfo
 * @param {object[]} interfaceList
 * @returns {object[]}
 */
export const enrichOpenVpnVlanInfo = (vlanInfo = [], interfaceList = []) => {
  const normalized = (vlanInfo || []).map(normalizeOpenVpnVlanEntry).filter(Boolean);
  const interfaceByVlanId = new Map();
  filterOpenVpnTapInterfaceList(interfaceList).forEach((row) => {
    const iface = String(row?.interface || row?.vlanName || '').trim();
    const rowVlanId = normalizeVlanIdForCompare(row?.vlanId ?? row?.vlanid ?? row?.vlanName ?? iface);
    if (rowVlanId) {
      interfaceByVlanId.set(rowVlanId, iface || `VLAN${rowVlanId}`);
    }
  });

  return normalized.map((vlan) => {
    const vlanId = normalizeVlanIdForCompare(vlan.vlanid ?? vlan.vlanId ?? vlan.id);
    const interfaceFromList = vlanId ? interfaceByVlanId.get(vlanId) : '';
    const interfaceLabel = interfaceFromList || (vlanId ? `VLAN${vlanId}` : String(vlan.name || '').trim());
    return normalizeOpenVpnVlanEntry({
      ...vlan,
      interface: interfaceLabel
    });
  });
};

/**
 * Resolve vlanInfo entry for OpenVPN TAP interface selection (VLAN1, vlanid, name, interface).
 * @param {object[]} vlanInfo
 * @param {string} tapVlanInterface
 * @param {object[]} [interfaceList]
 * @returns {object|null}
 */
export const resolveVlanByTapInterface = (vlanInfo, tapVlanInterface, interfaceList = []) => {
  if (!Array.isArray(vlanInfo) || !vlanInfo.length || !tapVlanInterface) return null;
  const enriched = enrichOpenVpnVlanInfo(vlanInfo, interfaceList);
  const target = String(tapVlanInterface).trim().toUpperCase();
  const targetId = normalizeVlanIdForCompare(tapVlanInterface);

  const directMatch = enriched.find((vlan) => {
    const name = String(vlan.name || '')
      .trim()
      .toUpperCase();
    const iface = String(vlan.interface || '')
      .trim()
      .toUpperCase();
    const vlanId = normalizeVlanIdForCompare(vlan.vlanid ?? vlan.vlanId ?? vlan.id);
    const vlanLabel = vlanId ? `VLAN${vlanId}`.toUpperCase() : '';
    return name === target || iface === target || vlanLabel === target || (targetId !== '' && vlanId === targetId);
  });
  return directMatch || null;
};

/**
 * True when VLAN1 is included in VPN User access control (vlan-backed or custom subnet).
 * @param {Array<string|number>} vlanIds
 * @param {Array<{ ipAddress: string, netmask: string }>} networks
 * @param {object[]} vlanInfo
 */
export const isVlan1IncludedInAccessControl = (vlanIds, networks, vlanInfo) => {
  if (!Array.isArray(vlanIds) || !vlanIds.length) return false;
  const list = Array.isArray(vlanInfo) ? vlanInfo : [];
  const vlan1 = list.find(
    (v) =>
      String(v.vlanid ?? v.vlanId ?? '') === '1' ||
      String(v.name || '')
        .trim()
        .toUpperCase() === 'VLAN1'
  );

  return vlanIds.some((vid, idx) => {
    if (vid !== '' && vid != null) {
      if (vlanIdsMatch(vid, 1) || String(vid).toUpperCase() === 'VLAN1') return true;
      const vlanData = list.find((v) => vlanIdsMatch(v.vlanid ?? v.vlanId, vid));
      if (
        vlanData &&
        (String(vlanData.vlanid ?? vlanData.vlanId ?? '') === '1' ||
          String(vlanData.name || '')
            .trim()
            .toUpperCase() === 'VLAN1')
      ) {
        return true;
      }
      return false;
    }
    if (!vlan1 || !networks?.[idx]) return false;
    const { ipAddress, netmask } = networks[idx];
    if (!ipAddress || !netmask || !vlan1.ipaddr || !vlan1.netmask) return false;
    try {
      const vlan1Network = calculateNetworkIp(vlan1.netmask, vlan1.ipaddr);
      const entryNetwork = calculateNetworkIp(netmask, ipAddress);
      return vlan1Network === entryNetwork;
    } catch {
      return false;
    }
  });
};

/**
 * Build VPN server address options from GET_PR_OPENVPN_INFO (opnVpnInfo).
 * Used by OpenVPN, WireGuard, and aligned with IPSec local-endpoint patterns.
 * @param {object|null|undefined} info - Response details from pr/v1/opnVpnInfo
 * @returns {{ label: string, value: string }[]}
 */
export const buildServerAddressOptions = (info) => {
  if (!info) return [];
  const options = [];
  if (info.ddnsProfile?.length > 0) {
    info.ddnsProfile.forEach((item) => options.push({ label: `DDNS: ${item}`, value: item }));
  }
  if (info.wanOneIpAddr) {
    options.push({ label: `WAN 1 Address: ${info.wanOneIpAddr} (Primary)`, value: info.wanOneIpAddr });
  }
  if (info.wanTwoIpAddr) {
    options.push({ label: `WAN 2 Address: ${info.wanTwoIpAddr} (Primary)`, value: info.wanTwoIpAddr });
  }
  if (info.externalIpAddress?.length > 0) {
    info.externalIpAddress.forEach((ip, idx) => options.push({ label: `External Public Address ${idx + 1}: ${ip}`, value: ip }));
  }
  if (info.wanOneIpAddrsArr?.length > 0) {
    info.wanOneIpAddrsArr.forEach((ip) => options.push({ label: `WAN 1 IP Address: ${ip}`, value: ip }));
  }
  if (info.wanTwoIpAddrsArr?.length > 0) {
    info.wanTwoIpAddrsArr.forEach((ip) => options.push({ label: `WAN 2 IP Address: ${ip}`, value: ip }));
  }
  return options;
};

const toPrNonEmptyStringList = (value) => (Array.isArray(value) ? value.filter((item) => typeof item === 'string' && item !== '') : []);

/**
 * WAN IPv6 choices of one VPN family from GET_PR_OPENVPN_INFO (R11-VPN-Overview.md 5.2(b), 6.4).
 * `family`: 'sitetosite' | 'clienttosite' | 'openvpn' | 'wireguard' | 'certificate'.
 * WireGuard derives the values from `wanIpv6ConfigList` (`ipv6Active`; `enabled` is ignored, Device UI
 * WireGuard.jsx 143-147); every other family reads the base keys. Absent keys (an old backend or a
 * closed read gate) give `false` / `[]`, so no IPv6 choice is offered.
 * External addresses: certificates keep every value in the IPv4 list (Certificates.jsx 126-133); the
 * other families split them as SitetoSite.jsx 208-224 (IPv4 when the IPv4 port passes, else IPv6
 * when the GUA / ULA check passes, else neither).
 * @returns {{ wanIpv6Enabled: boolean, wanOneIpv6AddrsArr: string[], wanTwoIpv6AddrsArr: string[], externalIpv4Addrs: string[], externalIpv6Addrs: string[] }}
 */
export const getPrWanIpv6Choices = (info, family) => {
  const source = info && typeof info === 'object' ? info : {};
  let wanIpv6Enabled;
  let wanOneIpv6AddrsArr;
  let wanTwoIpv6AddrsArr;
  if (family === 'wireguard') {
    const list = Array.isArray(source.wanIpv6ConfigList) ? source.wanIpv6ConfigList : [];
    const activeAddresses = (wanIdx) => {
      const entry = list.find((item) => Number(item?.wanIdx) === wanIdx && item?.ipv6Active === true);
      return entry ? toPrNonEmptyStringList(entry.addresses) : [];
    };
    wanIpv6Enabled = list.some((item) => item?.ipv6Active === true);
    wanOneIpv6AddrsArr = activeAddresses(1);
    wanTwoIpv6AddrsArr = activeAddresses(2);
  } else {
    wanIpv6Enabled = source.wanIpv6Enabled === true;
    wanOneIpv6AddrsArr = toPrNonEmptyStringList(source.wanOneIpv6AddrsArr);
    wanTwoIpv6AddrsArr = toPrNonEmptyStringList(source.wanTwoIpv6AddrsArr);
  }
  const external = toPrNonEmptyStringList(source.externalIpAddress);
  const externalIpv4Addrs = [];
  const externalIpv6Addrs = [];
  external.forEach((value) => {
    if (family === 'certificate' || !isPrIpv4FormatInvalid(value)) {
      externalIpv4Addrs.push(value);
    } else if (isValidPrIpv6Address(value)) {
      externalIpv6Addrs.push(value);
    }
  });
  return { wanIpv6Enabled, wanOneIpv6AddrsArr, wanTwoIpv6AddrsArr, externalIpv4Addrs, externalIpv6Addrs };
};

/** Device UI address-list filter per IP version (DUI:pages/vpn/sitetosite/BasicSettings.jsx 957-977). */
const isPrVpnAddressOfVersion = (value, ipVersion) => {
  if (!value) return false;
  const hasDot = value.includes('.');
  const hasColon = value.includes(':');
  if (ipVersion === 'ipv6') return hasColon && !hasDot;
  if (ipVersion === 'dualstack') return hasDot || hasColon;
  return hasDot && !hasColon;
};

/**
 * VPN address options with IPv6 choices (R11-VPN-Overview.md 6.4). Same labels and order as
 * buildServerAddressOptions for IPv4, then `WAN 1 IPv6 Address: x` / `WAN 2 IPv6 Address: x` and the
 * external IPv6 addresses. Each option carries `family: 'ipv4' | 'ipv6' | 'name'` (DDNS). The list is
 * filtered exactly like the Device UI, so DDNS names are dropped in `ipv6` mode (kept quirk).
 * `formatMessage` (optional) localizes the IPv6 WAN label (`prVpnWanIpv6AddressOption`).
 * @returns {{ label: string, value: string, family: string }[]}
 */
export const buildPrVpnAddressOptions = (info, { ipVersion = 'ipv4', family, formatMessage } = {}) => {
  if (!info) return [];
  const choices = getPrWanIpv6Choices(info, family);
  const options = [];
  const push = (label, value, optionFamily) => options.push({ label, value, family: optionFamily });
  toPrNonEmptyStringList(info.ddnsProfile).forEach((item) => push(`DDNS: ${item}`, item, 'name'));
  if (info.wanOneIpAddr) push(`WAN 1 Address: ${info.wanOneIpAddr} (Primary)`, info.wanOneIpAddr, 'ipv4');
  if (info.wanTwoIpAddr) push(`WAN 2 Address: ${info.wanTwoIpAddr} (Primary)`, info.wanTwoIpAddr, 'ipv4');
  let externalIndex = 0;
  choices.externalIpv4Addrs.forEach((ip) => {
    externalIndex += 1;
    push(`External Public Address ${externalIndex}: ${ip}`, ip, 'ipv4');
  });
  toPrNonEmptyStringList(info.wanOneIpAddrsArr).forEach((ip) => push(`WAN 1 IP Address: ${ip}`, ip, 'ipv4'));
  toPrNonEmptyStringList(info.wanTwoIpAddrsArr).forEach((ip) => push(`WAN 2 IP Address: ${ip}`, ip, 'ipv4'));
  const ipv6Label = (wan, address) =>
    formatMessage
      ? formatMessage({ id: 'prVpnWanIpv6AddressOption', defaultMessage: 'WAN {wan} IPv6 Address: {address}' }, { wan, address })
      : `WAN ${wan} IPv6 Address: ${address}`;
  choices.wanOneIpv6AddrsArr.forEach((ip) => push(ipv6Label(1, ip), ip, 'ipv6'));
  choices.wanTwoIpv6AddrsArr.forEach((ip) => push(ipv6Label(2, ip), ip, 'ipv6'));
  choices.externalIpv6Addrs.forEach((ip) => {
    externalIndex += 1;
    push(`External Public Address ${externalIndex}: ${ip}`, ip, 'ipv6');
  });
  return options.filter((option) => isPrVpnAddressOfVersion(option.value, ipVersion));
};

/**
 * Default server address when saved config is blank: primary WAN1, else first option from buildServerAddressOptions.
 * @param {object|null|undefined} info
 * @param {{ label: string, value: string }[]} options
 * @returns {string}
 */
export const getDefaultServerAddress = (info, options) => {
  if (!info) return '';
  const primaryWan = (info.wanOneIpAddr || '').trim();
  if (primaryWan) return primaryWan;
  return options[0]?.value || '';
};

/**
 * PUT tour completion for the current Insight user (cookies / axiosInstance).
 * Dynamic import avoids a circular dependency: useApi imports getUserRole from this module.
 * @returns {Promise<object|void>} Response body, or undefined when useApi handles 8056 / 44030.
 */
export async function putTourCompleted() {
  const { default: useApi } = await import('../hooks/useApi');
  const data = await useApi('FIRST_LOGIN_TOUR_STATUS', {}, { data: { tourCompleted: true } }, ['_Id']);
  if (data && data?.response?.status !== true && data?.response?.responseCode !== '200') {
    console.warn('[FIRST_LOGIN_TOUR_STATUS] unexpected response', data);
  }
  return data;
}

export default CommonMethods;
